import { c as _c } from "react/compiler-runtime";
import { spacing } from '@cc-shopping/ui/theme';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import type { Odds } from '.';
import { ApprovalLabel } from './ApprovalLabel';
import { EXCELLENT_RANGE, FAIR_RANGE, GOOD_RANGE, POOR_RANGE } from './constants';
type StyledApprovalLineProps = {
  isBlue: boolean;
  hasCreditScore: boolean;
  isBelowMin: boolean;
};
const StyledApprovalLine = styled('div')<StyledApprovalLineProps>(({
  theme,
  isBlue,
  hasCreditScore,
  isBelowMin
}) => {
  let backgroundColor = theme.palette.grey[200];
  if (isBlue) {
    backgroundColor = theme.palette.blues.dark;
    if (hasCreditScore) {
      if (isBelowMin) {
        backgroundColor = theme.palette.reds.main;
      } else {
        backgroundColor = theme.palette.primary.main;
      }
    }
  }
  return {
    display: 'inline-block',
    height: '6px',
    backgroundColor,
    width: '100%',
    gridRow: 2
  };
});
function PoorApprovalLine(t0) {
  const $ = _c(4);
  const {
    odds: t1,
    hasCreditScore,
    isBelowMin
  } = t0;
  const {
    min
  } = t1;
  const isPoor = min <= POOR_RANGE.max;
  let t2;
  if ($[0] !== hasCreditScore || $[1] !== isBelowMin || $[2] !== isPoor) {
    t2 = <StyledApprovalLine isBlue={isPoor} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} data-testid="poor-approval-line" />;
    $[0] = hasCreditScore;
    $[1] = isBelowMin;
    $[2] = isPoor;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function FairApprovalLine(t0) {
  const $ = _c(4);
  const {
    odds: t1,
    hasCreditScore,
    isBelowMin
  } = t0;
  const {
    min,
    max
  } = t1;
  const isFair = min <= FAIR_RANGE.max && max >= FAIR_RANGE.min;
  let t2;
  if ($[0] !== hasCreditScore || $[1] !== isBelowMin || $[2] !== isFair) {
    t2 = <StyledApprovalLine isBlue={isFair} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} data-testid="fair-approval-line" />;
    $[0] = hasCreditScore;
    $[1] = isBelowMin;
    $[2] = isFair;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function GoodApprovalLine(t0) {
  const $ = _c(4);
  const {
    odds: t1,
    hasCreditScore,
    isBelowMin
  } = t0;
  const {
    min,
    max
  } = t1;
  const isGood = min <= GOOD_RANGE.max && max >= GOOD_RANGE.min;
  let t2;
  if ($[0] !== hasCreditScore || $[1] !== isBelowMin || $[2] !== isGood) {
    t2 = <StyledApprovalLine isBlue={isGood} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} data-testid="good-approval-line" />;
    $[0] = hasCreditScore;
    $[1] = isBelowMin;
    $[2] = isGood;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function ExcellentApprovalLine(t0) {
  const $ = _c(4);
  const {
    odds: t1,
    hasCreditScore,
    isBelowMin
  } = t0;
  const {
    max
  } = t1;
  const isExcellent = max >= EXCELLENT_RANGE.min;
  let t2;
  if ($[0] !== hasCreditScore || $[1] !== isBelowMin || $[2] !== isExcellent) {
    t2 = <StyledApprovalLine isBlue={isExcellent} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} data-testid="excellent-approval-line" />;
    $[0] = hasCreditScore;
    $[1] = isBelowMin;
    $[2] = isExcellent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
type ApprovalLineProps = {
  labelStart?: number;
  labelEnd?: number;
  odds: Odds;
  hasCreditScore: boolean;
  isBelowMin: boolean;
  smallText?: boolean;
  isAmexOffer?: boolean;
};
export const ApprovalLine = t0 => {
  const $ = _c(69);
  const {
    labelStart,
    labelEnd,
    hasCreditScore,
    odds,
    isBelowMin,
    smallText,
    isAmexOffer
  } = t0;
  const {
    min,
    max
  } = odds;
  const moreThanOneChiclet = labelEnd && labelStart !== labelEnd - 1;
  let t1;
  if ($[0] !== labelStart || $[1] !== moreThanOneChiclet) {
    t1 = {
      ...(moreThanOneChiclet ? {
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        width: "100%",
        gridRow: 1,
        gridColumn: "span 4",
        textAlign: "left"
      } : {
        display: "flex",
        gridColumnStart: labelStart,
        gridColumnEnd: "span 2",
        justifyContent: labelStart && labelStart <= 2 ? "flex-start" : "flex-end",
        overflow: "visible"
      })
    };
    $[0] = labelStart;
    $[1] = moreThanOneChiclet;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== labelStart || $[4] !== moreThanOneChiclet) {
    t2 = moreThanOneChiclet && {
      sx: {
        gridColumnStart: labelStart
      }
    };
    $[3] = labelStart;
    $[4] = moreThanOneChiclet;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = spacing(2);
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = isAmexOffer ? 0 : 1;
  const t5 = isAmexOffer ? "none" : "auto";
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      marginRight: t3,
      opacity: t4,
      pointerEvents: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = !isAmexOffer ? min : "&nbsp;";
  let t8;
  if ($[10] !== hasCreditScore || $[11] !== smallText || $[12] !== t6 || $[13] !== t7) {
    t8 = <ApprovalLabel hasCreditScore={hasCreditScore} smallText={smallText} additionalStyles={t6}>{t7}</ApprovalLabel>;
    $[10] = hasCreditScore;
    $[11] = smallText;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t2 || $[16] !== t8) {
    t9 = <Box {...t2} textAlign="left">{t8}</Box>;
    $[15] = t2;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== labelEnd || $[19] !== moreThanOneChiclet) {
    t10 = labelEnd && moreThanOneChiclet && {
      sx: {
        gridColumnStart: labelEnd - 1
      }
    };
    $[18] = labelEnd;
    $[19] = moreThanOneChiclet;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = isAmexOffer ? "none" : "block";
  let t12;
  if ($[21] !== t11) {
    t12 = {
      display: t11
    };
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = !isAmexOffer ? max : "&nbsp;";
  let t14;
  if ($[23] !== hasCreditScore || $[24] !== smallText || $[25] !== t12 || $[26] !== t13) {
    t14 = <ApprovalLabel hasCreditScore={hasCreditScore} smallText={smallText} additionalStyles={t12}>{t13}</ApprovalLabel>;
    $[23] = hasCreditScore;
    $[24] = smallText;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t14) {
    t15 = <Box {...t10} textAlign="right">{t14}</Box>;
    $[28] = t10;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t1 || $[32] !== t15 || $[33] !== t9) {
    t16 = <Box sx={t1}>{t9}{t15}</Box>;
    $[31] = t1;
    $[32] = t15;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== max || $[36] !== min) {
    t17 = {
      min,
      max
    };
    $[35] = max;
    $[36] = min;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== hasCreditScore || $[39] !== isBelowMin || $[40] !== t17) {
    t18 = <PoorApprovalLine odds={t17} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} />;
    $[38] = hasCreditScore;
    $[39] = isBelowMin;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== max || $[43] !== min) {
    t19 = {
      min,
      max
    };
    $[42] = max;
    $[43] = min;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== hasCreditScore || $[46] !== isBelowMin || $[47] !== t19) {
    t20 = <FairApprovalLine odds={t19} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} />;
    $[45] = hasCreditScore;
    $[46] = isBelowMin;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== max || $[50] !== min) {
    t21 = {
      min,
      max
    };
    $[49] = max;
    $[50] = min;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== hasCreditScore || $[53] !== isBelowMin || $[54] !== t21) {
    t22 = <GoodApprovalLine odds={t21} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} />;
    $[52] = hasCreditScore;
    $[53] = isBelowMin;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== max || $[57] !== min) {
    t23 = {
      min,
      max
    };
    $[56] = max;
    $[57] = min;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== hasCreditScore || $[60] !== isBelowMin || $[61] !== t23) {
    t24 = <ExcellentApprovalLine odds={t23} hasCreditScore={hasCreditScore} isBelowMin={isBelowMin} />;
    $[59] = hasCreditScore;
    $[60] = isBelowMin;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t16 || $[64] !== t18 || $[65] !== t20 || $[66] !== t22 || $[67] !== t24) {
    t25 = <>{t16}{t18}{t20}{t22}{t24}</>;
    $[63] = t16;
    $[64] = t18;
    $[65] = t20;
    $[66] = t22;
    $[67] = t24;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  return t25;
};